import { BodyZone, MuscleTag } from '@/generated';
import { muscleTagFilter } from '@/utils/muscle-tag-filter';
import { Box, Button, TextField, Select, MenuItem, Grid, Stack, IconButton } from '@mui/material';
import { EditMuscleTagModal } from '../EditMuscleTagModal';
import { MuscleInfoCard } from './MuscleInfoCard';
import { MuscleTagEditor } from './MuscleTagEditor';
import { useContext, useEffect, useReducer, useState } from 'react';
import { AppContext } from '@/contexts/app.context';
import { ExerciseCard } from '../ExerciseCard';
import { isAdminUser, isTrainerUser } from '@/utils/user';
import { filterBodyzones } from '@/utils/bodyzone-filter';
import { CannotDeleteTagWarning, DeleteDialog } from '@/components/TagDeleteDialog';
import CreateIcon from '@mui/icons-material/Create';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAllExercisesQuery, useBodyZoneListQuery, useMuscleTagsQuery } from '@/hooks/query-hooks';
import { pathMap } from '@/navigation/paths';
import { useCreateMuscleTag, useDeleteMuscleTag, useUpdateMuscleTag } from '@/hooks/muscle-tag-mutation.hook';

const noZone: BodyZone = { id: 'nz', isDefaultZone: false, name: 'No Zone Filtered', zoneTagIds: [] };

interface TagListState {
  muscleTagList: Array<MuscleTag>;
  muscleTagMap: Map<string, MuscleTag>;
}

interface TagAction {
  type: 'init' | 'add' | 'edit' | 'delete';
  payload: any;
}

const tagListReducer = (state: TagListState, action: TagAction): TagListState => {
  switch (action.type) {
    case 'init':
      return action.payload;
    case 'add':
      return { muscleTagList: [...state.muscleTagList, action.payload], muscleTagMap: state.muscleTagMap.set(action.payload.id, action.payload) };
    case 'edit':
      return {
        muscleTagList: state.muscleTagList.map((bzt) => (bzt.id === action.payload.id ? action.payload : bzt)),
        muscleTagMap: state.muscleTagMap?.set(action.payload.id, action.payload),
      };
    case 'delete':
      state.muscleTagMap.delete(action.payload.id);
      return { muscleTagList: state.muscleTagList.filter((bzt) => bzt.id !== action.payload.id), muscleTagMap: state.muscleTagMap };
    default:
      return state;
  }
};

interface TabState {
  compound?: boolean;
  filteredBodyZone: BodyZone;
  filterName: string;
  tagEditMode: boolean;
  selectedMuscleTag?: MuscleTag;
}

const createEmptyTab = (): TabState => ({ compound: undefined, filterName: '', filteredBodyZone: noZone, tagEditMode: false, selectedMuscleTag: undefined });

const getStateFromLocalStorage = () => JSON.parse(localStorage.getItem('muscleTagsTabState') ?? JSON.stringify(createEmptyTab()));
const writeStateToLocalStorage = (tabState: TabState) => localStorage.setItem('muscleTagsTabState', JSON.stringify(tabState));

export const MuscleTagsTab = () => {
  const { state: contextState } = useContext(AppContext);
  const { muscleTagMap, muscleTags } = useMuscleTagsQuery();
  const { bodyZoneList } = useBodyZoneListQuery();
  const [state, dispatch] = useReducer(tagListReducer, { muscleTagList: muscleTags ?? [], muscleTagMap });
  const [tabState, setTabState] = useState<TabState>(getStateFromLocalStorage());
  const setAndWriteState = (tabState: TabState) => {
    setTabState(tabState);
    writeStateToLocalStorage(tabState);
  };

  const [cannotDelete, setCannotDelete] = useState<boolean>(false);
  const [tagToDelete, setTagToDelete] = useState<MuscleTag | undefined>(undefined);
  const [showTagEditModal, setShowTagEditModal] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const createMuscleTag = useCreateMuscleTag();
  const updateMuscleTag = useUpdateMuscleTag();
  const deleteMuscleTag = useDeleteMuscleTag();

  const muscleTagId = searchParams.get('muscleTagId');

  useEffect(() => {
    setAndWriteState({ ...tabState, selectedMuscleTag: muscleTags?.find((t) => t.id === tabState.selectedMuscleTag?.id) });
  }, [muscleTags]);

  useEffect(() => {
    dispatch({ type: 'init', payload: { muscleTagList: muscleTags, muscleTagMap: muscleTagMap } });
  }, [muscleTags]);

  useEffect(() => {
    if (muscleTags && muscleTagId) //TODO: Use Tanstack query
    {
      setAndWriteState({ ...tabState, selectedMuscleTag: muscleTags.find((mt) => mt.id === muscleTagId) });
    }
  }, [muscleTagId, muscleTags]);

  return (
    <>
      <CannotDeleteTagWarning open={cannotDelete} setOpen={setCannotDelete} />
      {tabState.selectedMuscleTag && (
        <EditMuscleTagModal
          open={showTagEditModal}
          initialMuscleTag={tabState.selectedMuscleTag}
          onClose={() => {
            setShowTagEditModal(false);
          }}
          muscleTagUpdated={() => {}}
        />
      )}
      <DeleteDialog
        open={tagToDelete !== undefined}
        handleClose={async (deleted) => {
          if (deleted) {
            if (!tagToDelete) return;
            await deleteMuscleTag.mutateAsync(tagToDelete.id);
            // const deleteOk = true;

            // if (deleteOk) {
            dispatch({ type: 'delete', payload: tagToDelete });
            // } else {
            //   setCannotDelete(true);
            // }
          }
          setTagToDelete(undefined);
        }}
      />

      <Grid rowGap={2} columnGap={1.5} container>
        <Grid size={12}>
          {isAdminUser(contextState?.userProfile) && (
            <Box>
              <Button onClick={() => setAndWriteState({ ...tabState, tagEditMode: !tabState.tagEditMode })}>
                {tabState.tagEditMode ? 'Edit Mode (Click to enter selection mode)' : 'Selection Mode (Click to enter Edit mode)'}{' '}
              </Button>
              <IconButton
                sx={{ m: 1 }}
                onClick={() => {
                  if (isAdminUser(contextState?.userProfile)) setShowTagEditModal(true);
                }}
              >
                <CreateIcon />
              </IconButton>
            </Box>
          )}
        </Grid>
        <Grid size={3} sx={{}}>
          <Stack gap={1}>
            <TextField
              sx={{ my: 1 }}
              label="No Muscle Name Filter"
              autoFocus={true}
              value={tabState.filterName}
              onChange={(e: any) => setAndWriteState({ ...tabState, filterName: e.target.value })}
            />
            <Select
              value={tabState.compound}
              displayEmpty
              onChange={(e) => {
                const newValue = e.target.value === undefined ? undefined : e.target.value === 'true';
                setAndWriteState({ ...tabState, compound: newValue });
              }}
            >
              <MenuItem key={'true'} value={'true'}>
                Compound Muscles
              </MenuItem>
              <MenuItem key={'false'} value={'false'}>
                Non Compound Muscles
              </MenuItem>
              <MenuItem key={''} value={undefined}>
                All
              </MenuItem>
            </Select>

            <Select value={tabState.filteredBodyZone.id}>
              {[noZone, ...filterBodyzones(bodyZoneList ?? [])].map((bz) => (
                <MenuItem
                  key={bz.id}
                  value={bz.id}
                  selected={tabState.filteredBodyZone.id === bz.id}
                  onClick={(e) => {
                    setAndWriteState({ ...tabState, filteredBodyZone: bz });
                  }}
                >
                  {bz.name}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Grid>
        <Grid size={8}>
          {muscleTags && (
            <MuscleTagEditor
              tagEditMode={tabState.tagEditMode}
              tagClicked={(tag) => {
                setAndWriteState({ ...tabState, selectedMuscleTag: tag });
              }}
              tagCreated={async (tagName: string) => {
                const newTag = await createMuscleTag.mutateAsync({ name: tagName });
                dispatch({ type: 'add', payload: newTag });
              }}
              tagRemoved={setTagToDelete}
              tagUpdated={async (tag: MuscleTag) => {
                await updateMuscleTag.mutateAsync(tag);
                dispatch({ type: 'edit', payload: tag });
              }}
              muscleTags={muscleTagFilter(state.muscleTagList, tabState.filterName, tabState.filteredBodyZone, tabState.compound)}
            />
          )}
        </Grid>
        <Grid size={12}>
          <MuscleInfoCard
            onTagClick={(tag) => {
              setAndWriteState({ ...tabState, selectedMuscleTag: tag });
            }}
            muscleTag={tabState.selectedMuscleTag}
          />
        </Grid>
        <Grid size={12}>
          {(isAdminUser(contextState?.userProfile) || isTrainerUser(contextState?.userProfile)) && (
            <Grid sx={{}}>{tabState.selectedMuscleTag?.exemplaryExerciseId && <ExampleExercise exerciseId={tabState.selectedMuscleTag?.exemplaryExerciseId} />}</Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const ExampleExercise = (props: { exerciseId: string }) => {
  const { exercises } = useAllExercisesQuery(true);
  const exercise = exercises?.find((e) => props.exerciseId === e.id);
  const navigate = useNavigate();

  return (
    <Grid>
      {exercise && (
        <ExerciseCard
          onClick={() => {
            navigate(`${pathMap.ExerciseDetails}/${exercise.id}`);
          }}
          exercise={exercise}
          timeout={1}
        />
      )}
    </Grid>
  );
};
